<template>
  <article class="listing-card" @click="onCardClick">
    <div class="listing-card__media">
      <div
        v-if="images.length"
        ref="track"
        class="listing-card__slider"
        @scroll.passive="onScroll"
      >
        <!-- slides are links (tap navigates, swipe scrolls), hidden from
             assistive tech as duplicates of the title link -->
        <a
          v-for="(image, index) in images"
          :key="index"
          class="listing-card__slide"
          :href="listing.url"
          tabindex="-1"
          aria-hidden="true"
        >
          <img class="listing-card__image" :src="image" alt="" loading="lazy" draggable="false" />
        </a>
      </div>
      <div v-else class="listing-card__image listing-card__image--empty">
        <SvgIcon name="home" :size="32" />
        <span class="listing-card__no-image">{{ t('listing-card.no-image') }}</span>
      </div>

      <div class="listing-card__chips">
        <span v-if="listing.status" class="listing-card__status" :class="statusModifier">
          <span class="listing-card__status-dot" />
          {{ listing.status }}
        </span>
        <span v-if="phaseLabel" class="listing-card__status" :data-status="phaseLabel">
          <span class="listing-card__status-dot" />
          {{ phaseLabel }}
        </span>
      </div>

      <template v-if="images.length > 1">
        <button
          v-if="current > 0"
          type="button"
          class="listing-card__nav listing-card__nav--prev"
          :aria-label="t('listing-card.image-prev')"
          @click="goTo(current - 1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
        <button
          v-if="current < images.length - 1"
          type="button"
          class="listing-card__nav listing-card__nav--next"
          :aria-label="t('listing-card.image-next')"
          @click="goTo(current + 1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
        <div class="listing-card__dots">
          <button
            v-for="(image, index) in images"
            :key="index"
            type="button"
            class="listing-card__dot"
            :class="{ 'is-active': index === current }"
            :aria-label="t('listing-card.image-goto', { index: index + 1 })"
            @click="goTo(index)"
          />
        </div>
      </template>
    </div>

    <div class="listing-card__body">
      <div class="listing-card__heading">
        <h3 class="listing-card__title">
          <a :href="listing.url" class="listing-card__link">{{ title }}</a>
        </h3>
        <p class="listing-card__location">
          {{ location.main
          }}<span v-if="location.phase" class="listing-card__phase"> – {{ location.phase }}</span>
        </p>
      </div>

      <p class="listing-card__price">
        <span v-if="price.prefix" class="listing-card__price-prefix">{{ price.prefix }}</span>
        <strong>{{ price.value }}</strong>
        <span v-if="price.suffix" class="listing-card__price-suffix">{{ price.suffix }}</span>
      </p>

      <dl v-if="specs.length" class="listing-card__specs">
        <div v-for="spec in specs" :key="spec.label" class="listing-card__spec">
          <dt class="listing-card__spec-label">{{ spec.label }}</dt>
          <dd class="listing-card__spec-value">{{ spec.value }}</dd>
        </div>
      </dl>
    </div>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { useDetail } from '../lib/useDetail';
import { housingTitleWithNumber } from '../lib/housingTitle';
import { t } from '../lib/i18n';
import {
  listingPrice,
  listingSpecs,
  listingStatusModifier,
  registrationPhaseLabel
} from '../lib/listingCardFields';

const props = defineProps({
  listing: { type: Object, required: true }
});

const { open } = useDetail();

// The whole card opens the detail panel. The title and slide links keep their
// href (cmd/ctrl-click and crawlers still reach the real detail page), so
// their default only gets prevented on a plain click. Buttons are the
// carousel controls and keep their own behaviour.
const onCardClick = (event) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.target.closest('button')) return;
  event.preventDefault();
  open(props.listing);
};

const images = computed(() =>
  (props.listing.images || []).map((image) => image.url || image.src || image).filter(Boolean)
);

const track = ref(null);
const current = ref(0);

const onScroll = () => {
  const el = track.value;
  if (el) current.value = Math.round(el.scrollLeft / el.clientWidth);
};

const goTo = (index) => {
  const el = track.value;
  if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
};

const title = computed(() => housingTitleWithNumber(props.listing));

const location = computed(() => {
  const { city, project, subProject } = props.listing;
  return { main: [city, project].filter(Boolean).join(', '), phase: subProject || '' };
});

const price = computed(() => listingPrice(props.listing));

const statusModifier = computed(() => listingStatusModifier(props.listing.status));

const phaseLabel = computed(() => registrationPhaseLabel(props.listing.subProjectStatus));

const specs = computed(() => listingSpecs(props.listing));
</script>
